import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { WidgetVector, WidgetDisplayMode, WidgetKeys } from 'jamespot-user-api';
import { WidgetEditor, Widget } from 'jamespot-front-business';
// eslint-disable-next-line import/no-cycle
import WidgetWrapperCore from '../WidgetWrapperCore';
import { CSSWidgetLayer } from './WidgetLayer.style';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const BETA_JRCDoubleClick = commonComponentsRegistry.getLazyComponent('DoubleClick');

/*
    Widget layering
*/

export const WidgetLayer = ({ uniqid, mode }: { uniqid: string; mode: WidgetDisplayMode }) => {
    const layers = useAppSelector((state) => state.widgets.ids[uniqid].widget.layers, shallowEqual);
    return (
        <>
            {layers?.map((layer, index) => {
                if (mode === 'edit') {
                    return <Layer uniqid={layer} key={index} mode={'edit'} />;
                }

                return <Layer uniqid={layer} key={index} mode={'edit'} />;
            })}
        </>
    );
};

const Layer = ({ uniqid, mode }: { uniqid: string; mode: WidgetDisplayMode }) => {
    const layer = useAppSelector((state) => state.widgets.layers[uniqid]);
    if (mode === 'view') {
        return (
            <CSSWidgetLayer opacity={1} backgroundColor={'transparent'} pointerEvents={'none'}>
                {layer.widgets.map((widgetWrapper, index) => {
                    return (
                        <LayerWidget
                            key={index}
                            layerId={layer.uniqid}
                            uniqid={widgetWrapper.widget.uniqid}
                            mode={'view'}
                        />
                    );
                })}
            </CSSWidgetLayer>
        );
    }

    return (
        <CSSWidgetLayer
            opacity={layer.visible ? 1 : 0}
            backgroundColor={layer.visible ? 'rgba(255,255,255,.1)' : 'transparent'}
            pointerEvents={layer.locked ? 'none' : 'all'}>
            {layer.widgets.map((widgetWrapper, index) => {
                return (
                    <LayerWidget
                        key={index}
                        layerId={layer.uniqid}
                        uniqid={widgetWrapper.widget.uniqid}
                        mode={'edit'}
                    />
                );
            })}
        </CSSWidgetLayer>
    );
};

/*
    Widget from the layer
*/
const LayerWidget = ({ uniqid, layerId, mode }: { uniqid: string; layerId: string; mode: WidgetDisplayMode }) => {
    const dispatch = useAppDispatch();
    const widgetWrapper = useAppSelector((state) => state.widgets.ids[uniqid], shallowEqual);
    const layer = useAppSelector((state) => state.widgets.layers[layerId]);

    if (!widgetWrapper) {
        return <></>;
    }

    if (!layer) {
        return <></>;
    }

    if (!layer.position) {
        return <></>;
    }

    const onDoubleTap = (uniqid: string, name: WidgetKeys) => {
        /*
            Open editor
        */
        dispatch(WidgetEditor.slice.actions.registerEditor({ uniqid, name }));
    };

    const onMouseUp = (position: WidgetVector) => {
        dispatch(Widget.slice.actions.updateLayer({ uniqid: layerId, props: { position } }));
    };

    if (mode === 'view') {
        return (
            <div style={{ position: 'absolute', top: layer.position.y, left: layer.position.x }}>
                <WidgetWrapperCore
                    uniqid={uniqid}
                    widget={widgetWrapper}
                    mode={mode}
                    inplace={true}
                    context={'article'}
                />
            </div>
        );
    }

    return (
        <DraggableHandler
            position={{ x: layer.position.x, y: layer.position.y }}
            onMouseUp={(position: WidgetVector) => {
                if (
                    (layer.position && layer.position.x !== position.x) ||
                    (layer.position && layer.position.y !== position.y)
                ) {
                    onMouseUp(position);
                }
            }}>
            <BETA_JRCDoubleClick
                onDoubleTap={() => onDoubleTap(widgetWrapper.widget.uniqid, widgetWrapper.widget.name)}>
                <WidgetWrapperCore
                    uniqid={uniqid}
                    widget={widgetWrapper}
                    mode={mode}
                    inplace={true}
                    context={'article'}
                />
            </BETA_JRCDoubleClick>
        </DraggableHandler>
    );
};

export type Vector = {
    x: number;
    y: number;
};

// TODO: COMBINE WITH THE ONE USED ON COLOR PICKER
// TODO: COMBINE WITH THE ONE USED ON COLOR PICKER
// TODO: COMBINE WITH THE ONE USED ON COLOR PICKER
export const DraggableHandler = ({
    position,
    children,
    onMouseUp,
}: {
    position: Vector;
    children: ReactElement;
    onMouseUp: (position: Vector) => void;
}) => {
    const [mouseIsDown, setMouseDown] = useState<boolean>(false);
    const [mouse, setMouse] = useState<Vector>({ x: 0, y: 0 });
    const [start, setStart] = useState<Vector>({ x: 0, y: 0 });
    const [child, setChild] = useState<Vector>({ x: position.x, y: position.y });

    const ref = useRef<HTMLDivElement>(null);

    const handleMouseDown = useCallback(
        (event: any) => {
            setMouseDown(true);
            if (ref.current) {
                setMouse({ x: event.nativeEvent.clientX, y: event.nativeEvent.clientY });
                setStart({ x: child.x, y: child.y });
            }
        },
        [child.x, child.y],
    );

    const handleMouseMove = useCallback(
        (event: any) => {
            if (mouseIsDown && ref.current) {
                const dx = start.x + (event.clientX - mouse.x);
                const dy = start.y + (event.clientY - mouse.y);

                setChild({ x: dx, y: dy });
            }
        },
        [mouse.x, mouse.y, mouseIsDown, start.x, start.y],
    );

    const handleMouseUp = useCallback(() => {
        setMouseDown(false);
        onMouseUp({ x: child.x, y: child.y });
    }, [child.x, child.y, onMouseUp]);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return (
        <div style={{ position: 'absolute', top: child.y, left: child.x }} ref={ref} onMouseDown={handleMouseDown}>
            {children}
        </div>
    );
};
